import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

import {
  Button,
  Divider,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMutation } from "@apollo/client";
import colors from "app/colors";
import {
  InternalAdminEditUserMutation,
  InternalAdminEditUserMutationVariables,
  InternalAdminUserAttributeFragment,
  UserAttributeFragment,
} from "gql/graphql";
import { useOverlay } from "modules/providers/overlay-provider";
import INTERNAL_ADMIN_EDIT_USER_MUTATION from "../_networking/internal-admin-edit-user";
import useInternalAdminUsers from "../hooks/use-internal-admin-users";
import useInternalAdminOrganizations from "../hooks/use-internal-admin-organizations";
import { sortUserAttributeCategories } from "modules/utils/Functions";
import useInternalAdminUserAttributes from "../hooks/use-internal-admin-user-attributes";
import useInternalAdminUserAttributeCategoryList from "../hooks/use-internal-admin-user-attribute-category-list";
import InternalAdminUserAttributesSelector from "../user/internal-admin-user-attributes-selector";

interface IProps {
  userId: string;
}

const InternalAdminEditUserAttributesModal = ({ userId }: IProps) => {
  const { closeModal } = useOverlay();

  const { user } = useInternalAdminUsers(userId);
  const { organization } = useInternalAdminOrganizations(user?.organizationId);
  const { userAttributes } = useInternalAdminUserAttributes(
    user?.userAttributeIdList,
  );
  const { userAttributeCategories } = useInternalAdminUserAttributeCategoryList(
    {
      organizationId: user?.organizationId,
      input: {
        limit: 100000,
        offset: 0,
      },
    },
  );

  const [_userAttributes, setUserAttributes] = useState<
    InternalAdminUserAttributeFragment[]
  >([]);

  useEffect(() => {
    setUserAttributes(userAttributes);
  }, [userAttributes]);

  const _userAttributeCategories = useMemo(() => {
    if (!organization) return [];
    return [...userAttributeCategories].sort(
      sortUserAttributeCategories(organization),
    );
  }, [organization, userAttributeCategories]);

  const userAttributesByUserAttributeCategoryId = useMemo(
    () =>
      _userAttributes.reduce(
        (acc, userAttribute) => {
          if (userAttribute.userAttributeCategoryId) {
            acc[userAttribute.userAttributeCategoryId] = [
              ...(acc[userAttribute.userAttributeCategoryId] || []),
              userAttribute,
            ];
          }
          return acc;
        },
        {} as Record<string, UserAttributeFragment[]>,
      ),
    [_userAttributes],
  );

  const [internalAdminEditUserMutationAPI, { loading }] = useMutation<
    InternalAdminEditUserMutation,
    InternalAdminEditUserMutationVariables
  >(INTERNAL_ADMIN_EDIT_USER_MUTATION);

  const onSaveClicked = useCallback(async () => {
    if (!user) return;
    const userAttributeIdList = _userAttributes.map(
      (userAttribute) => userAttribute.id,
    );
    internalAdminEditUserMutationAPI({
      variables: {
        organizationId: user.organizationId,
        id: user.id,
        input: {
          userAttributeIdList,
        },
      },
      onError: () => {
        //TODO: handle error
      },
      onCompleted: () => {
        closeModal();
      },
    });
  }, [closeModal, internalAdminEditUserMutationAPI, user, _userAttributes]);

  if (!user) return null;

  return (
    <Modal isOpen onClose={closeModal} size="3xl" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalBody>
          <Stack w="full" spacing={4} py={6} alignItems="center">
            <VStack w="full">
              <Text
                fontFamily="Outfit"
                fontWeight={700}
                fontSize={24}
                color={colors.blackSecondary}
              >
                Edit User Attributes
              </Text>
            </VStack>

            <VStack
              w="full"
              spacing={4}
              pt={4}
              px={4}
              divider={<Divider />}
              maxH={500}
              overflowY="auto"
              pb={100}
            >
              {_userAttributeCategories.map((userAttributeCategory) => {
                const userAttributesInCategory =
                  userAttributesByUserAttributeCategoryId
                    ? userAttributesByUserAttributeCategoryId[
                        userAttributeCategory.id
                      ] || []
                    : [];

                const _setUserAttributes = (
                  newUserAttributes: InternalAdminUserAttributeFragment[],
                ) => {
                  //need to remove the tags in the category that are not in the new tags
                  setUserAttributes((prevUserAttributes) => {
                    const updatedUserAttributes = [
                      ...prevUserAttributes,
                    ].filter(
                      (userAttribute) =>
                        userAttribute.userAttributeCategoryId !==
                        userAttributeCategory.id,
                    );
                    updatedUserAttributes.push(...newUserAttributes);
                    return updatedUserAttributes;
                  });
                };

                return (
                  <HStack spacing={2} w="full" key={userAttributeCategory.id}>
                    <Text
                      minW={200}
                      color={colors.blackSecondary}
                      fontWeight={700}
                      fontSize={14}
                      noOfLines={1}
                    >
                      {userAttributeCategory.name}
                    </Text>
                    <InternalAdminUserAttributesSelector
                      userAttributeCategory={userAttributeCategory}
                      onSelectedOptionsChanged={_setUserAttributes}
                      defaultValue={userAttributesInCategory}
                    />
                  </HStack>
                );
              })}
            </VStack>
            <HStack w="full" spacing={4} justifyContent="center">
              <Button
                fontWeight={700}
                fontSize={16}
                variant="cancel"
                height={50}
                w={140}
                onClick={closeModal}
              >
                Cancel
              </Button>
              <Button
                fontWeight={700}
                fontSize={16}
                height={50}
                w={140}
                onClick={onSaveClicked}
              >
                {loading ? <Spinner size="sm" /> : "Save Attributes"}
              </Button>
            </HStack>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(InternalAdminEditUserAttributesModal);
